import { Link } from "react-router-dom";
import { Target, Heart, Award, ShieldCheck, Users, Sparkles, ArrowRight } from "lucide-react";

const VALUES = [
    { icon: Award, title: "Quality first", text: "Every product, every line of code, every cable run – we obsess over it." },
    { icon: ShieldCheck, title: "Integrity", text: "Transparent pricing. Clear timelines. No hidden surprises." },
    { icon: Users, title: "Customer obsession", text: "Your success is the metric we use to measure ours." },
    { icon: Sparkles, title: "Innovation", text: "We constantly explore, test and adopt better ways to deliver." },
];

const TIMELINE = [
    { year: "2014", title: "Founded in Kampala", text: "Started as a small home detergent producer with 3 founders." },
    { year: "2017", title: "ICT division launched", text: "Opened our IT services unit serving SMBs across Kampala." },
    { year: "2020", title: "Software development", text: "Built our first commercial mobile app for an East African logistics client." },
    { year: "2023", title: "National distribution", text: "Detergent line distributed in all major Ugandan districts." },
    { year: "2026", title: "Regional expansion", text: "Now serving customers in Uganda, Kenya, Rwanda & Tanzania." },
];

const TEAM = [
    { name: "James Mwesigwa", role: "Founder & CEO", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80" },
    { name: "Sarah Birungi", role: "COO", img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=600&q=80" },
    { name: "David Okello", role: "Head of ICT", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=600&q=80" },
    { name: "Grace Atim", role: "Head of Software", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=600&q=80" },
];

export default function About() {
    return (
        <div data-testid="about-page">
            {/* Header */}
            <section className="bg-ink text-white py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-12 items-end">
                    <div>
                        <div className="overline text-primary mb-4">About us</div>
                        <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1] tracking-tight">
                            Built in Uganda.<br/>
                            <span className="text-primary">For East Africa.</span>
                        </h1>
                    </div>
                    <p className="text-lg text-white/70 leading-relaxed">
                        Peppa Target Services Limited is a multi-domain Ugandan company manufacturing premium home
                        detergents, delivering enterprise ICT services, and building software that helps East African
                        businesses thrive in the digital era.
                    </p>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-8">
                <div className="border-l-4 border-primary pl-8" data-testid="vision-block">
                    <Target className="w-8 h-8 text-primary mb-4" />
                    <div className="overline text-primary mb-3">Our vision</div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">
                        To be East Africa's most trusted multi-sector enterprise.
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                        We believe one company can do many things well – when each division is led by experts who care.
                    </p>
                </div>
                <div className="border-l-4 border-secondary pl-8" data-testid="mission-block">
                    <Heart className="w-8 h-8 text-secondary mb-4" />
                    <div className="overline text-secondary mb-3">Our mission</div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">
                        Deliver excellence across products, services and code.
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                        We measure ourselves by the outcomes our customers achieve – cleaner homes, safer networks, shipped apps.
                    </p>
                </div>
            </section>

            {/* Values */}
            <section className="bg-surface py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-4">Our values</div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold mb-12 max-w-3xl">What we hold ourselves to.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {VALUES.map((v) => (
                            <div key={v.title} className="bg-white border border-border p-8" data-testid={`value-${v.title.toLowerCase().replace(/\s/g, '-')}`}>
                                <v.icon className="w-7 h-7 text-primary mb-4" />
                                <h3 className="font-display font-bold text-lg mb-2">{v.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32">
                <div className="overline text-primary mb-4">Our journey</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold mb-12">A decade of building.</h2>
                <div className="relative pl-8 border-l-2 border-border space-y-12">
                    {TIMELINE.map((t) => (
                        <div key={t.year} className="relative" data-testid={`timeline-${t.year}`}>
                            <span className="absolute -left-[2.55rem] top-1 w-4 h-4 bg-primary rounded-full border-4 border-white" />
                            <div className="font-display font-bold text-3xl text-primary mb-1">{t.year}</div>
                            <h3 className="font-display font-bold text-xl mb-2">{t.title}</h3>
                            <p className="text-muted-foreground leading-relaxed max-w-2xl">{t.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Team */}
            <section className="bg-surface py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-4">Leadership</div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold mb-12">The people behind Peppa Target.</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {TEAM.map((m) => (
                            <div key={m.name} className="group" data-testid={`team-${m.name.toLowerCase().replace(/\s/g, '-')}`}>
                                <div className="aspect-[3/4] overflow-hidden mb-4 bg-surface">
                                    <img src={m.img} alt={m.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 grayscale group-hover:grayscale-0" />
                                </div>
                                <div className="font-display font-bold text-lg">{m.name}</div>
                                <div className="text-sm text-muted-foreground">{m.role}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-ink text-white py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-8 items-center">
                    <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight">
                        Let's build<br/>something together.
                    </h2>
                    <div>
                        <p className="text-white/70 mb-6 max-w-md">
                            Whether you need bulk detergents, an ICT audit, or a custom app – our teams are ready.
                        </p>
                        <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-4 font-semibold hover:bg-primary-hover transition-colors" data-testid="about-cta-contact">
                            Contact us <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
